import type { PageResult } from 'liyu-pc-base/api/typing';
import { remoteSelectStore, useUserStore } from 'liyu-pc-base/init-plugin';
import { message } from 'ant-design-vue';

/**
 * 从嵌套对象中按路径取值。
 * 支持点号写法 a.b.c 和数组索引写法 params[0].name。
 */
export function getNestedValue(obj: any, path: string): any {
  if (!path) return undefined;
  const tokens = path.match(/[^.\[\]]+|\[\d+\]/g);
  if (!tokens) return undefined;
  return tokens.reduce((current, token) => {
    if (token.startsWith('[')) {
      return current?.[parseInt(token.slice(1, -1), 10)];
    }
    return current?.[token];
  }, obj);
}

/**
 * 解析相对于 baseKeyPath 的表达式，从 context 中获取值。
 *
 * baseKeyPath 是扁平路径段数组：string 段为对象属性名，number 段为数组索引。
 * 例如 ["formModel", "formtemplate_75589", 0, "formtemplate_37262", 0] 表示
 * 当前节点位于 context.formModel.formtemplate_75589[0].formtemplate_37262[0]。
 *
 * 表达式语法（以 @ 开头时触发）：
 *
 * 1. @<key>.<path>
 *    在 baseKeyPath 中找到 <key>，构建从 context 到该 key（含自动跟随的数组索引）的完整路径，再追加 <path>。
 *    示例: baseKeyPath = ["formModel", "a", 0, "b", 0]
 *    @a.name        → context.formModel.a[0].name
 *    @b.name        → context.formModel.a[0].b[0].name
 *    @formModel.x   → context.formModel.x          （formModel 后面不是数字，不加索引）
 *
 * 2. @<key>[<index>].<path>
 *    同上，但将该层级的数组索引覆盖为 <index>，用于获取同级数组其他项的属性。
 *    @b[1].name     → context.formModel.a[0].b[1].name
 *    @a[2].name     → context.formModel.a[2].name
 *
 * 3. @<level><rest>  （level 为数字，按路径段逐层回退）
 *    每个路径段是一个 key 或一个 index，@N 表示从末尾去掉 N 个段。
 *    rest 可以是 .property 或 [index].property 等追加路径。
 *    @0.name        → context.formModel.a[0].b[0].name   （当前项）
 *    @1             → context.formModel.a[0].b            （退掉最后的 [0]，得到数组）
 *    @1[1].name     → context.formModel.a[0].b[1].name    （同级数组第1项的 name）
 *    @2.name        → context.formModel.a[0].name         （再退掉 b，得到父项）
 *
 * 4. 不以 @ 开头
 *    向后兼容：直接作为完整路径从 context 中取值。
 *    a[0].b[0].name → context.a[0].b[0].name
 */
export function resolveContextValue(selectName: string, context: any, baseKeyPath: any[]): any {
  if (!selectName) return undefined;

  // 不以 @ 开头：向后兼容，直接作为完整路径解析
  if (!selectName.startsWith('@')) {
    return getNestedValue(context, selectName);
  }

  const basePath = baseKeyPath || [];
  if (!basePath || basePath.length === 0) {
    return getNestedValue(context, selectName.slice(1));
  }

  // 去掉 @ 前缀
  const expr = selectName.slice(1);

  // 辅助函数：将 baseKeyPath 的段数组构建为路径字符串
  // string 段 → .propertyName，number 段 → [index]
  const buildPathFromSegments = (segments: any[]): string => {
    let path = '';
    for (const seg of segments) {
      if (typeof seg === 'number') {
        path += `[${seg}]`;
      } else {
        path += path ? `.${seg}` : seg;
      }
    }
    return path;
  };

  // 尝试匹配数字层级语法：@<level><rest>
  const levelMatch = expr.match(/^(\d+)(.*)$/);
  if (levelMatch) {
    const [, levelStr, restRaw] = levelMatch;
    const level = parseInt(levelStr, 10);
    const keepSegments = basePath.length - level;

    let fullPath = '';
    if (keepSegments > 0) {
      fullPath = buildPathFromSegments(basePath.slice(0, keepSegments));
    }

    if (restRaw) {
      if (restRaw.startsWith('.') || restRaw.startsWith('[')) {
        fullPath += restRaw;
      } else {
        fullPath += `.${restRaw}`;
      }
    }

    if (keepSegments <= 0 && !restRaw) {
      return context;
    }
    return getNestedValue(context, fullPath);
  }

  // 匹配 key 名称语法：@<key>[<index>].<path> 或 @<key>.<path>
  const keyMatch = expr.match(/^([a-zA-Z_]\w*)(?:\[(\d+)\])?\.?(.*)$/);
  if (!keyMatch) {
    return getNestedValue(context, expr);
  }

  const [, targetKey, keyIdxOverride, keyRestPath] = keyMatch;

  // 在 baseKeyPath 中查找 targetKey（从后往前，取最深的匹配）
  let targetKeyIndex = -1;
  for (let i = basePath.length - 1; i >= 0; i--) {
    if (basePath[i] === targetKey) {
      targetKeyIndex = i;
      break;
    }
  }

  if (targetKeyIndex === -1) {
    return getNestedValue(context, expr);
  }

  // 构建从 context 到目标 key 的完整路径（包含 key 本身及其前面的所有段）
  const segments = basePath.slice(0, targetKeyIndex + 1);
  let fullPath = buildPathFromSegments(segments);

  // 处理 key 后面的数组索引：
  // - 如果手动指定了 index（@key[N]），用指定的 N
  // - 否则如果 key 后面紧跟一个数字段，自动包含该索引（key[index]）
  if (keyIdxOverride !== undefined) {
    fullPath += `[${keyIdxOverride}]`;
  } else if (targetKeyIndex + 1 < basePath.length && typeof basePath[targetKeyIndex + 1] === 'number') {
    fullPath += `[${basePath[targetKeyIndex + 1]}]`;
  }

  if (keyRestPath) {
    fullPath += `.${keyRestPath}`;
  }
  return getNestedValue(context, fullPath);
}

const obj2json = (obj: any): string => {
  if (obj instanceof Array) {
    return obj.join();
  } else if (obj instanceof Object) {
    return JSON.stringify(obj);
  } else {
    return obj.toString();
  }
};

const obj2str = (obj: any): string => {
  if (obj instanceof Array) {
    return obj.join();
  } else if (typeof obj === 'number') {
    return String(obj);
  } else if (obj === undefined) {
    return 'undefined';
  } else if (obj === null) {
    return 'null';
  } else if (typeof obj === 'boolean') {
    return String(obj);
  } else if (typeof obj === 'object') {
    return Object.keys(obj)
      .sort((a, b) => a.localeCompare(b))
      .map((key: string) => {
        return `${key}-${obj2json(obj[key])}`;
      })
      .join();
  } else {
    console.error('error type' + typeof obj);
    return obj.toString();
  }
};

export default (api_fun: any, api_url?: string): any => {
  const fun = (params: Record<string, any>, noCache?: boolean): Promise<PageResult> => {
    const userStore = useUserStore();
    const org = userStore.currentOrg;
    const paramsKey = Object.keys(params)
      .sort((a, b) => a.localeCompare(b))
      .map((key: string) => {
        return `${key}-${obj2str(params[key])}`;
      })
      .join();
    const time = new Date().getTime() + 3600 * 1000;
    const obj = remoteSelectStore().get(`${org?.id}_${api_url}`);

    return new Promise(resolve => {
      if (!noCache && obj && obj.time > new Date().getTime() && obj.paramsKey == paramsKey) {
        resolve(obj.data);
      } else {
        api_fun(params)
          .then((res: any) => {
            remoteSelectStore().set(`${org?.id}_${api_url}`, {
              data: res,
              time: time,
              paramsKey: paramsKey,
            });
            resolve(res);
          })
          .catch(() => {
            message.destroy();
            message.error('接口调用失败', 10);
          });
      }
    });
  };
  return fun;
};
